import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { generatePath } from 'react-router'
import { useDispatch } from 'react-redux'

import {
  WASTE_RECORD_LINE_ITEM_EDIT,
  DELIVERY_RECORD_LINE_ITEM_EDIT
} from 'portal/routes'
import { NavButtonDecorator, Spacer } from 'portal/lib/primitives'
import { EditButton } from './EditButton'
import { RemoveButton } from './RemoveButton'
import { recordItemRemovedToggle } from 'portal/store/records/editing/actions'
import { useModuleIds, useUserProjectRole } from 'portal/lib/hooks'
import { RestoreButton } from './RestoreButton'
import { MODULES } from 'portal/lib/constants'

export function LineItemButtons({ record, index, isImperial = false }) {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()
  const { isReadOnly } = useUserProjectRole(accountId, projectId)

  const items = record.data.items ?? record.data.products
  const lineItem = items[index]

  const editUri = useMemo(
    () =>
      MODULES.select(moduleName, {
        waste: () =>
          generatePath(WASTE_RECORD_LINE_ITEM_EDIT, {
            accountId,
            projectId,
            recordId: record.recordId,
            moduleName,
            lineItem: index
          }),
        delivery: () =>
          generatePath(DELIVERY_RECORD_LINE_ITEM_EDIT, {
            accountId,
            projectId,
            recordId: record.recordId,
            moduleName,
            lineItem: index
          })
      }),
    [accountId, index, moduleName, projectId, record.recordId]
  )

  const handleRemoval = useCallback(() => {
    dispatch(
      recordItemRemovedToggle(
        accountId,
        projectId,
        moduleName,
        record,
        index,
        isImperial,
        !lineItem.isRemoved
      )
    )
  }, [
    accountId,
    dispatch,
    index,
    isImperial,
    lineItem,
    moduleName,
    projectId,
    record
  ])

  return (
    <>
      {(!isReadOnly || record.canEdit) && (
        <>
          <NavButtonDecorator to={editUri}>
            <EditButton disabled={lineItem.isRemoved} iconOnly={true} />
          </NavButtonDecorator>
          <Spacer.Tiny />
        </>
      )}

      {(!isReadOnly || record.canEdit) &&
        (lineItem.isRemoved ? (
          <RestoreButton
            data-testid="RestoreButton"
            iconOnly={true}
            onClick={handleRemoval}
          >
            Restore
          </RestoreButton>
        ) : (
          <RemoveButton
            id="RemoveButton"
            iconOnly={true}
            onClick={handleRemoval}
          >
            Remove
          </RemoveButton>
        ))}
    </>
  )
}

LineItemButtons.propTypes = {
  record: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired
}
